import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { MatRippleModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { FilterType } from '../../models/datacenter-filters';
import { pageTransitions } from '../../animations/page-transitions';
import { AdditionalInput } from '../../models/datacenter-aggrid-view';
import { ViewNumericFilterComponent }
	from '../../datacenter/view-customization/filters/view-numeric-filter.component';
import { ViewDateFilterComponent }
	from '../../datacenter/view-customization/filters/view-date-filter.component';
import { ViewFreeTextFilterComponent }
	from '../../datacenter/view-customization/filters/view-free-text-filter.component';

import { SuggestiveListFilterComponent }
	from './filters/suggestive-list-filter.component';
import { DropdownFilterComponent }
	from './filters/view-dropdown-filter.component';

@Component({
	selector: 'copo-product-list-filter-customization',
	templateUrl: './product-list-filter-customization.component.html',
	styleUrl: './product-list-filter-customization.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		NgFor,
		NgIf,
		ViewFreeTextFilterComponent,
		ViewDateFilterComponent,
		ViewNumericFilterComponent,
		SuggestiveListFilterComponent,
		DropdownFilterComponent,
		MatIconModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule,
		MatRippleModule
	]
})
export class ProductListFilterCustomizationComponent implements OnInit, OnDestroy {
	@Input() allFilters!: FilterType[];
	@Input() addedFilters!: FilterType[];
	@Input() additionalInput?: AdditionalInput;
	searchText: string | undefined = '';
	searchTextControl = new FormControl('');
	filtersToShow: FilterType[] = [];
	booleanFilterToHide: string[] = [];
	private searchTextSubscription?: Subscription;

	ngOnInit() {
		this.filtersToShow = this.allFilters.toSorted(
			(f1, f2) => f1.label.localeCompare(f2.label)
		);
		for (const filterToAdd of this.addedFilters) {
			if (filterToAdd.filterType === 'Boolean') {
				this.booleanFilterToHide.push(filterToAdd.filterId);
				this.filtersToShow = this.filtersToShow.filter(filter => filter.filterId !== filterToAdd.filterId);
			}
		}
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged()
		).subscribe(value => {
			this.searchText = <string | undefined> value;
			if (this.searchText === undefined) {
				return;
			}

			this.filtersToShow = this.setFiltersToShow(
				this.allFilters.filter(filter => !this.booleanFilterToHide.includes(filter.filterId)),
				this.searchText
			);
		});
	}

	addValueFilter(filter: FilterType) {
		if (filter.filterType === 'Boolean' || filter.filterType === 'DocumentLastConnectionDate') {
			this.booleanFilterToHide.push(filter.filterId);
			this.filtersToShow = this.filtersToShow.filter(filterToHide => filterToHide.filterId !== filter.filterId);
		}
		const addedFilter = this.addFilter(filter);
		addedFilter.addEmptyValue();
	}

	removeFilter(filter: FilterType) {
		this.addedFilters.splice(this.addedFilters.indexOf(filter), 1);
		if (filter.filterType === 'Boolean' || filter.filterType === 'DocumentLastConnectionDate') {
			this.booleanFilterToHide = this.booleanFilterToHide.filter(filterId => filterId !== filter.filterId);
			this.filtersToShow.push(filter);
		}
	}

	setFiltersToShow(filters: FilterType[], searchText: string) {
		return filters.filter(filter => {
			if (filter.label) {
				return filter.label.replace(/\u00A0/g, ' ').toLowerCase()
					.search(searchText.toLowerCase()) >= 0;
			}
			return false;
		});
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	private addFilter(filter: FilterType) {
		let addedFilter = this.addedFilters.find(value => value.filterId === filter.filterId);
		if (!addedFilter) {
			addedFilter = filter;
			this.addedFilters.push(filter);
		}
		return addedFilter;
	}
}
